import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useRegisterInput from '../Form/useRegisterInput';
import { useFormContext } from '../Form';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import MaterialSwitch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import IconButton from '@material-ui/core/IconButton';
import ErrorMessage from '../ErrorMessage';
import Icon from '../Icon';
import isDefined from '@afosto/utils/isDefined';
import clsx from 'clsx';
import styled from 'styled-components';

const StyledFormControl = styled(FormControl)`
  && {
    display: flex;
    flex-direction: row;

    &.inline {
      &:hover {
        .inlineEditField-adornment {
          visibility: visible !important;
          opacity: 1;
        }
      }
    }
  }
`;

const StyledFormGroup = styled(FormGroup)`
  && {
    flex: 1;
  }
`;

const HiddenIconButton = styled(IconButton)`
  && {
    visibility: hidden;
    opacity: 0;
    transition: ${props =>
      props.theme.transitions.create('opacity', {
        easing: props.theme.transitions.easing.sharp,
        duration: props.theme.transitions.duration.shortest,
      })};
  }
`;

const Switch = ({ fullWidth, margin, register: shouldRegister, changeOnMounted, ...props }) => {
  const formContext = useFormContext();
  const {
    editInline: formInline = false,
    isEditing: formIsEditing = true,
    errors = {},
    setIsEditing,
    setValue,
    watch,
  } = formContext || {};
  const { value } = props;
  const {
    name,
    inline = formInline,
    isEditing = formIsEditing,
    error = !!errors[name],
    errorMessage,
    onEdit,
    readOnly,
    helperText,
    checked = value ?? (watch && watch(name)) ?? false,
    onChange,
    ...inputProps
  } = props;
  const initialValue = value ?? (watch && watch(name));
  const isInline = inline && !isEditing;
  const showEditAdornment = inline && !isEditing && !readOnly;
  const formContextRef = useRef(formContext);
  const onChangeRef = useRef(onChange);
  const checkedRef = useRef(checked);
  const changeOnMountedRef = useRef(changeOnMounted);
  const initialValueRef = useRef(initialValue);

  useRegisterInput(name, shouldRegister);

  useEffect(() => {
    formContextRef.current = formContext;
    onChangeRef.current = onChange;
    checkedRef.current = checked;
    changeOnMountedRef.current = changeOnMounted;
    initialValueRef.current = initialValue;
  });

  useEffect(() => {
    if (!isDefined(initialValueRef.current) && onChangeRef.current && changeOnMountedRef.current) {
      onChangeRef.current(checkedRef.current ?? false);
    }
  }, []);

  /**
   * Handle the change on the select.
   */
  const handleOnChange = () => {
    if (formContext && !onChange) {
      setValue(name, !checked);
      return;
    }

    if (onChange) {
      onChange({}, !checked);
    }
  };

  /**
   * On edit handler.
   */
  const handleOnEdit = () => {
    if (formContext && !onEdit) {
      setIsEditing(true);
    }

    if (onEdit) {
      onEdit();
    }
  };

  /**
   * Return the error message.
   */
  const getErrorMessage = () => {
    if (formContext && !errorMessage) {
      return errors[name] && <ErrorMessage errors={errors} name={name} />;
    }

    return errorMessage;
  };

  if (isInline) {
    inputProps.disabled = true;
  }

  if (readOnly) {
    inputProps.disabled = true;
  }

  return (
    <StyledFormControl
      error={error}
      fullWidth={fullWidth}
      className={clsx({ inline: isInline || (inline && readOnly) })}
    >
      <StyledFormGroup>
        <FormControlLabel
          control={
            <MaterialSwitch {...inputProps} checked={checked} onChange={() => handleOnChange()} />
          }
          label={inputProps.label}
        />
        {helperText && !error && <FormHelperText>{helperText}</FormHelperText>}
        {error && getErrorMessage() && <FormHelperText>{getErrorMessage()}</FormHelperText>}
      </StyledFormGroup>
      {showEditAdornment && (
        <div>
          <HiddenIconButton className="inlineEditField-adornment" onClick={handleOnEdit} edge="end">
            <Icon icon={['fal', 'pencil']} fontSize="small" />
          </HiddenIconButton>
        </div>
      )}
    </StyledFormControl>
  );
};

Switch.propTypes = {
  name: PropTypes.string.isRequired,
  changeOnMounted: PropTypes.bool,
  inline: PropTypes.bool,
  isEditing: PropTypes.bool,
  readOnly: PropTypes.bool,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  fullWidth: PropTypes.bool,
  margin: PropTypes.string,
  onEdit: PropTypes.func,
  onChange: PropTypes.func,
  checked: PropTypes.bool,
  register: PropTypes.bool,
  value: PropTypes.any,
};

Switch.defaultProps = {
  changeOnMounted: true,
  inline: undefined,
  isEditing: undefined,
  readOnly: false,
  helperText: undefined,
  error: undefined,
  errorMessage: undefined,
  fullWidth: true,
  margin: 'normal',
  onEdit: undefined,
  onChange: undefined,
  checked: undefined,
  register: true,
  value: undefined,
};

export default Switch;
